import React, { FC, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ERoutes } from "@utils/constants";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { useIsClicked } from "@/hooks/useIsClicked";
import { CREATOR_CREDIT_TEXT } from "@/utils/creatorCreditText";
import { creditText, paidCreditLine } from "@/utils/creditText";

import * as S from "./styles";

const isDev = process.env.NODE_ENV === "development";

interface INextStepsPage {
  composer: string;
  songName: string;
  videoUrl: string;
  spotifyUrl: string;
  downloadStatus?: "idle" | "started" | "complete" | "error";
  // The track is already licensed (e.g. downloaded from Orders) — the buyer
  // owes the one-line paid credit, so the flow shows that plus the
  // connect-your-video instruction.
  licensed?: boolean;
}

/* ── Shared icons ── */

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

/* Credit text box with an always-visible custom scrollbar on phone (iOS hides
   the native one). Used for the clean credit boxes (no inline copy button). */
const CreditBox: FC<{ children: React.ReactNode }> = ({ children }) => (
  <S.CreditScrollWrap>
    <S.CreditTextBox>{children}</S.CreditTextBox>
    <S.CreditScrollbar />
  </S.CreditScrollWrap>
);

/* ── Step item helper ── */

const StepItemRow: FC<{ num: number; title: React.ReactNode; desc: string; last?: boolean; children?: React.ReactNode }> = ({
  num,
  title,
  desc,
  last,
  children,
}) => (
  <S.StepItem>
    <S.StepNumCol>
      <S.StepNum>{num}</S.StepNum>
    </S.StepNumCol>
    <S.StepContent $last={last}>
      <S.StepContentTitle>{title}</S.StepContentTitle>
      <S.StepContentDesc>{desc}</S.StepContentDesc>
      {children}
    </S.StepContent>
  </S.StepItem>
);

/* ── Claims warning (shown in last step of every tier) ── */

const ClaimsWarning: FC = () => (
  <S.ClaimsWarningBox>
    <strong>Important:</strong> Never appeal our claims in YouTube Studio — claims are managed exclusively on the "Claims" page and can be fully resolved there.
  </S.ClaimsWarningBox>
);

/* ── One-line paid credit block (Subscription + Single Licence) ──
   The short credit these tiers must keep in the video description. Same box +
   copy pattern as the Free Use block, one line instead of six. */
const PaidCreditBlock: FC = () => {
  const { isClicked, handleClick } = useIsClicked();
  return (
    <S.CreditBlock>
      <CreditBox>{paidCreditLine}</CreditBox>
      <S.CopyBtn onClick={() => { handleClick(); navigator.clipboard.writeText(paidCreditLine); }}>
        {isClicked ? (<><CheckIcon /> Copied!</>) : (<><CopyIcon /> Copy</>)}
      </S.CopyBtn>
    </S.CreditBlock>
  );
};

/* ═══════════════════════════════════
   FREE
   - No channel connections
   - Manual video submission only
   - Extended credit text required
   ═══════════════════════════════════ */

const FreeNextSteps: FC<{ composer: string; songName: string; videoUrl: string; spotifyUrl: string }> = ({
  composer,
  songName,
  videoUrl,
  spotifyUrl,
}) => {
  const { isClicked, handleClick } = useIsClicked();
  const navigate = useNavigate();
  const fullCreditText = creditText(`${composer} - ${songName}`, videoUrl, spotifyUrl);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px", alignItems: "center" }}>
      <S.StepsWrapper>
      <S.StepsList>
        <StepItemRow
          num={1}
          title={"Upload your YouTube video and add credit text to description"}
          desc=""
        >
          <S.CreditBlock>
            <CreditBox>{fullCreditText}</CreditBox>
            <S.CopyBtn
              onClick={() => { handleClick(); navigator.clipboard.writeText(fullCreditText); }}
            >
              {isClicked ? (<><CheckIcon /> Copied!</>) : (<><CopyIcon /> Copy</>)}
            </S.CopyBtn>
          </S.CreditBlock>
        </StepItemRow>

        <StepItemRow
          num={2}
          title={"Connect Your Video"}
          desc={'Go to your "Claims" page and add your YouTube Video URL. We will check it and remove the copyright claim if no issues were found.'}
          last
        >
          <ClaimsWarning />
        </StepItemRow>
      </S.StepsList>

      <S.PrimaryButton onClick={() => navigate(ERoutes.AccountVideos)}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#60a3d7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block", verticalAlign: "middle", marginRight: "8px", marginTop: "-2px" }}>
          <path d="M23 7l-7 5 7 5V7z" /><path d="M14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z" />
        </svg>
        Go to Claims page
      </S.PrimaryButton>
      </S.StepsWrapper>
    </div>
  );
};

/* ── Go to your Claims page button (shared) ── */

const GoToMyVideosButton: FC = () => {
  const navigate = useNavigate();
  return (
    <S.PrimaryButton onClick={() => navigate(ERoutes.AccountVideos)}>
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#60a3d7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block", verticalAlign: "middle", marginRight: "8px", marginTop: "-2px" }}>
        <path d="M23 7l-7 5 7 5V7z" /><path d="M14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z" />
      </svg>
      Go to your Claims page
    </S.PrimaryButton>
  );
};

/* ═══════════════════════════════════
   LICENSED (downloaded from Orders)
   - Owns the track → one-line paid credit
   - Then connect the video so claims clear
   ═══════════════════════════════════ */

const LicensedNextSteps: FC = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: "28px", alignItems: "center" }}>
    <S.StepsWrapper>
      <S.StepsList>
        <StepItemRow
          num={1}
          title={"Use the track and add the one-line credit"}
          desc={"Add the track to your YouTube video, and include this one-line credit in the description — keep it there for as long as the video uses the track:"}
        >
          <PaidCreditBlock />
        </StepItemRow>
        <StepItemRow
          num={2}
          title={"Connect Your Video"}
          desc={'Go to your "Claims" page and add your YouTube video URL. Once a claim is detected on it, you can release it instantly with a single click.'}
          last
        >
          <ClaimsWarning />
        </StepItemRow>
      </S.StepsList>
      <GoToMyVideosButton />
    </S.StepsWrapper>
  </div>
);

/* ── Timestamp example block (Creator) ── */

const SHORT_TIMESTAMP_EXAMPLE = "00:00 Patrick Lenk - Ethereal\n08:48 Simon Daum - Touched By The Divine";
const LONG_TIMESTAMP_EXAMPLE = "0:52:37 Simon Daum - Standing In Holy Places\n0:58:47 Simon Daum - Sacred Knowing\n1:03:16 Simon Daum - Eternal (Choir Version)";

const tsBoxStyle = { maxHeight: "none", whiteSpace: "pre-line" as const, wordBreak: "normal" as const, userSelect: "text" as const, WebkitUserSelect: "text" as const };

const TimestampExample: FC = () => {
  const { isClicked: shortCopied, handleClick: handleShortCopy } = useIsClicked();
  const { isClicked: longCopied, handleClick: handleLongCopy } = useIsClicked();

  return (
    <S.CreditBlock>
      <S.CreditTextBox style={{ ...tsBoxStyle, marginBottom: "8px", position: "relative" }}>
        {SHORT_TIMESTAMP_EXAMPLE}
        <S.InlineCopyBtn
          onClick={() => { handleShortCopy(); navigator.clipboard.writeText(SHORT_TIMESTAMP_EXAMPLE); }}
        >
          {shortCopied ? <CheckIcon /> : <CopyIcon />}
        </S.InlineCopyBtn>
      </S.CreditTextBox>

      <S.CreditTextBox style={{ ...tsBoxStyle, marginBottom: 0, position: "relative" }}>
        {LONG_TIMESTAMP_EXAMPLE}
        <S.InlineCopyBtn
          onClick={() => { handleLongCopy(); navigator.clipboard.writeText(LONG_TIMESTAMP_EXAMPLE); }}
        >
          {longCopied ? <CheckIcon /> : <CopyIcon />}
        </S.InlineCopyBtn>
      </S.CreditTextBox>
    </S.CreditBlock>
  );
};

/* ═══════════════════════════════════
   SUBSCRIPTION (formerly PREMIUM)
   - Multiple channels
   - 1-line credit text
   - Always show "connect more" link
   ═══════════════════════════════════ */

const PremiumNextSteps: FC<{ connected: boolean }> = ({ connected }) => {
  if (!connected) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "28px", alignItems: "center" }}>
        <S.StepsWrapper>
        <S.StepsList>
          <StepItemRow
            num={1}
            title="Upload your video and add the one-line credit"
            desc="Use the track in a YouTube video and upload it to any of your channels. Include this one-line credit in the description and keep it there while the video uses the track:"
          >
            <PaidCreditBlock />
          </StepItemRow>
          <StepItemRow
            num={2}
            title="Link your channels & manage your claims"
            desc={'Connect your YouTube channels on the "Claims" page. Any Audio Sanctum-related claims will then appear there for you to manage.'}
            last
          >
            <ClaimsWarning />
          </StepItemRow>
        </S.StepsList>
        <GoToMyVideosButton />
        </S.StepsWrapper>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px", alignItems: "center" }}>
      <S.StepsWrapper>
      <S.StepsList>
        <StepItemRow num={1} title="Upload your video" desc="Use the track in a YouTube video and upload it to any of your channels." />
        <StepItemRow
          num={2}
          title="Manage your claims"
          desc={'Any Audio Sanctum-related claims on your connected channels will appear in the "Claims" section, where you can manage them.'}
          last
        >
          <ClaimsWarning />
        </StepItemRow>
      </S.StepsList>

      <GoToMyVideosButton />
      </S.StepsWrapper>
    </div>
  );
};

/* ═══════════════════════════════════
   MUSIC CREATOR
   - Multiple channels (required)
   - Credit text + timestamps required
   - Auto-detection, reviewed by team
   ═══════════════════════════════════ */

const CreatorNextSteps: FC<{ connected: boolean }> = ({ connected }) => {
  const { isClicked, handleClick } = useIsClicked();

  if (!connected) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "28px", alignItems: "center" }}>
        <S.StepsWrapper>
        <S.StepsList>
          <StepItemRow
            num={1}
            title={"Upload your YouTube video and add credit text to description"}
            desc=""
          >
            <S.CreditBlock>
              <CreditBox>{CREATOR_CREDIT_TEXT}</CreditBox>
              <S.CopyBtn
                onClick={() => { handleClick(); navigator.clipboard.writeText(CREATOR_CREDIT_TEXT); }}
              >
                {isClicked ? (<><CheckIcon /> Copied!</>) : (<><CopyIcon /> Copy</>)}
              </S.CopyBtn>
            </S.CreditBlock>
          </StepItemRow>
          <StepItemRow
            num={2}
            title="Add timestamps to your description"
            desc={'Add timestamps to your video description following the format "TIME ARTIST - TITLE":'}
          >
            <TimestampExample />
          </StepItemRow>
          <StepItemRow
            num={3}
            title="Connect your channels"
            desc={'Connect your YouTube channels on the "Claims" page. Any videos on your connected channels will be detected automatically and reviewed by our team.'}
            last
          >
            <ClaimsWarning />
          </StepItemRow>
        </S.StepsList>
        <GoToMyVideosButton />
        </S.StepsWrapper>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px", alignItems: "center" }}>
      <S.StepsWrapper>
      <S.StepsList>
        <StepItemRow
          num={1}
          title={"Upload your YouTube video and add credit text to description"}
          desc=""
        >
          <S.CreditBlock>
            <CreditBox>{CREATOR_CREDIT_TEXT}</CreditBox>
            <S.CopyBtn
              onClick={() => { handleClick(); navigator.clipboard.writeText(CREATOR_CREDIT_TEXT); }}
            >
              {isClicked ? (<><CheckIcon /> Copied!</>) : (<><CopyIcon /> Copy</>)}
            </S.CopyBtn>
          </S.CreditBlock>
        </StepItemRow>
        <StepItemRow
          num={2}
          title="Add timestamps to your description"
          desc={'Add timestamps to your video description following the format "TIME ARTIST - TITLE":'}
        >
          <TimestampExample />
        </StepItemRow>
        <StepItemRow
          num={3}
          title="Your videos are detected automatically"
          desc={'Any videos on your connected channels will be detected automatically and reviewed by our team in the "Claims" section.'}
          last
        >
          <ClaimsWarning />
        </StepItemRow>
      </S.StepsList>

      <GoToMyVideosButton />
      </S.StepsWrapper>
    </div>
  );
};

/* ── Download status banner ── */

const DownloadBanner: FC<{ status: "idle" | "started" | "complete" | "error" }> = ({ status }) => {
  if (status === "idle") return null;

  const isComplete = status === "complete";
  const isError = status === "error";

  return (
    <div style={{
      width: "100%",
      textAlign: "center",
      padding: "10px 16px",
      borderRadius: "8px",
      fontFamily: "'Raleway', sans-serif",
      fontSize: "0.875rem",
      fontWeight: 600,
      marginBottom: "8px",
      transition: "all 0.3s ease",
      background: isError ? "rgba(228, 122, 106, 0.1)" : isComplete ? "rgba(52, 211, 153, 0.1)" : "rgba(156, 211, 253, 0.1)",
      color: isError ? "#e47a6a" : isComplete ? "#34d399" : "#9cd3fd",
      border: `1px solid ${isError ? "rgba(228, 122, 106, 0.2)" : isComplete ? "rgba(52, 211, 153, 0.2)" : "rgba(156, 211, 253, 0.2)"}`,
    }}>
      {isError ? "Download failed" : isComplete ? "Download complete" : "Download started..."}
    </div>
  );
};

/* ── Main export ── */

const NextStepsPage: FC<INextStepsPage> = ({ composer, songName, videoUrl, spotifyUrl, downloadStatus = "idle", licensed }) => {
  // Entitlements, not plan identity: an account awaiting reactivation is on
  // the subscription plan but is not paying for it, so it browses and
  // downloads on free terms until it reactivates (ToU 17.4).
  const { entitlementType: accountType } = useAccountType();
  const [devConnected, setDevConnected] = useState(true);

  // Free tier has no channel concept
  const showConnectedToggle = false && isDev && accountType !== AccountType.FREE;

  const banner = <DownloadBanner status={downloadStatus} />;

  const content = (() => {
    // Licensed download (from Orders): the buyer owns the track and owes no
    // credit text — only the connect-your-video step remains, for every plan.
    if (licensed) return <LicensedNextSteps />;
    switch (accountType) {
      case AccountType.FREE:
        return <FreeNextSteps composer={composer} songName={songName} videoUrl={videoUrl} spotifyUrl={spotifyUrl} />;
      case AccountType.SUBSCRIPTION:
        return <PremiumNextSteps connected={isDev ? devConnected : true} />;
      case AccountType.MUSIC_CREATOR:
        return <CreatorNextSteps connected={isDev ? devConnected : true} />;
      default:
        return <FreeNextSteps composer={composer} songName={songName} videoUrl={videoUrl} spotifyUrl={spotifyUrl} />;
    }
  })();

  return (
    <>
      {showConnectedToggle && (
        <S.DevConnectedToggle>
          <S.DevBadge>DEV</S.DevBadge>
          <S.DevTabContainer>
            <S.DevTab $active={devConnected} onClick={() => setDevConnected(true)}>Connected</S.DevTab>
            <S.DevTab $active={!devConnected} onClick={() => setDevConnected(false)}>Not Connected</S.DevTab>
          </S.DevTabContainer>
        </S.DevConnectedToggle>
      )}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <S.StepLabel>Step 2</S.StepLabel>
        <div style={{ width: "100%", marginTop: "12px", marginBottom: "24px" }}>{banner}</div>
      </div>
      {content}
    </>
  );
};

export default NextStepsPage;
